import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import { normalizeContentPackRecord, normalizeEntitlementRecord } from './contentPackAccess';
import { PLATFORM_COLLECTIONS } from './platformSchema';

export function normalizeSubscriptionPlan(record = {}, fallbackId = '') {
  const id = record.id || record.subscriptionPlanId || fallbackId || record.code || '';

  return {
    id,
    subscriptionPlanId: id,
    code: record.code || id,
    name: record.name || record.code || id,
    description: record.description || '',
    tier: record.tier || 'free',
    active: record.active !== false,
    billingInterval: record.billingInterval || 'none',
    basePrice: Number(record.basePrice || 0),
    currency: record.currency || 'USD',
    includedInstructorSeats: Number(record.includedInstructorSeats || 0),
    includedContentPackIds: Array.isArray(record.includedContentPackIds)
      ? record.includedContentPackIds.filter(Boolean)
      : [],
    addOnEligibleContentPackIds: Array.isArray(record.addOnEligibleContentPackIds)
      ? record.addOnEligibleContentPackIds.filter(Boolean)
      : [],
    featureFlags: record.featureFlags || {},
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export function normalizeOrganizationSubscription(record = {}, fallbackId = '') {
  const id = record.id || record.organizationSubscriptionId || fallbackId || '';

  return {
    id,
    organizationSubscriptionId: id,
    organizationId: record.organizationId || '',
    planCode: record.planCode || '',
    subscriptionPlanId: record.subscriptionPlanId || '',
    status: record.status || 'inactive',
    billingInterval: record.billingInterval || 'none',
    seatCount: Number(record.seatCount || 0),
    instructorSeatLimit: Number(record.instructorSeatLimit || 0),
    trialStartAt: record.trialStartAt || null,
    trialEndAt: record.trialEndAt || null,
    currentPeriodStartAt: record.currentPeriodStartAt || null,
    currentPeriodEndAt: record.currentPeriodEndAt || null,
    cancelAtPeriodEnd: Boolean(record.cancelAtPeriodEnd),
    entitlementSourceType: record.entitlementSourceType || '',
    entitlementSourceId: record.entitlementSourceId || '',
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export function isContentPackIncludedInPlan(plan = {}, contentPackId = '') {
  const normalizedPlan = normalizeSubscriptionPlan(plan);
  return normalizedPlan.includedContentPackIds.includes(contentPackId);
}

export function getEntitlementSourceMetadata(
  entitlement = {},
  {
    subscription = null,
    plan = null,
  } = {}
) {
  const normalizedEntitlement = normalizeEntitlementRecord(entitlement);
  const normalizedPlan = plan ? normalizeSubscriptionPlan(plan) : null;
  const normalizedSubscription = subscription
    ? normalizeOrganizationSubscription(subscription)
    : null;

  switch (normalizedEntitlement.sourceType) {
    case 'subscription':
      return {
        label: normalizedPlan?.name
          ? `Included in ${normalizedPlan.name}`
          : 'Included in subscription',
        sourceType: 'subscription',
        sourceId: normalizedEntitlement.sourceId || normalizedSubscription?.organizationSubscriptionId || '',
      };
    case 'trial':
      return {
        label: normalizedPlan?.name
          ? `${normalizedPlan.name} trial`
          : 'Trial access',
        sourceType: 'trial',
        sourceId: normalizedEntitlement.sourceId || normalizedSubscription?.organizationSubscriptionId || '',
      };
    case 'pack_addon':
      return {
        label: 'Pack add-on',
        sourceType: 'pack_addon',
        sourceId: normalizedEntitlement.sourceId || '',
      };
    case 'promotional':
      return {
        label: 'Promotional access',
        sourceType: 'promotional',
        sourceId: normalizedEntitlement.sourceId || '',
      };
    case 'sas_direct_grant':
      return {
        label: 'SAS direct grant',
        sourceType: 'sas_direct_grant',
        sourceId: normalizedEntitlement.sourceId || '',
      };
    case 'manual_grant':
    default:
      return {
        label: 'Manual grant',
        sourceType: normalizedEntitlement.sourceType || 'manual_grant',
        sourceId: normalizedEntitlement.sourceId || '',
      };
  }
}

export function deriveEntitlementsFromSubscription(subscription = {}, plans = []) {
  const normalizedSubscription = normalizeOrganizationSubscription(subscription);
  if (!normalizedSubscription.organizationId) return [];
  if (!['trialing', 'active'].includes(normalizedSubscription.status)) return [];

  const matchingPlan = (plans || [])
    .map((plan) => normalizeSubscriptionPlan(plan))
    .find(
      (plan) =>
        plan.code === normalizedSubscription.planCode ||
        plan.subscriptionPlanId === normalizedSubscription.subscriptionPlanId
    );

  if (!matchingPlan) return [];

  const sourceType = normalizedSubscription.status === 'trialing' ? 'trial' : 'subscription';
  const startDate =
    normalizedSubscription.status === 'trialing'
      ? normalizedSubscription.trialStartAt
      : normalizedSubscription.currentPeriodStartAt;
  const endDate =
    normalizedSubscription.status === 'trialing'
      ? normalizedSubscription.trialEndAt
      : normalizedSubscription.currentPeriodEndAt;

  return matchingPlan.includedContentPackIds.map((contentPackId) => ({
    organizationId: normalizedSubscription.organizationId,
    contentPackId,
    status: 'active',
    startDate,
    endDate,
    sourceType,
    sourceId: normalizedSubscription.organizationSubscriptionId,
    billingStatusSnapshot: normalizedSubscription.status,
    grantedReason:
      sourceType === 'trial'
        ? `Included via ${matchingPlan.name} trial`
        : `Included via ${matchingPlan.name}`,
  }));
}

export async function createSubscriptionPlan(
  db,
  {
    code = '',
    name = '',
    description = '',
    tier = 'free',
    active = true,
    billingInterval = 'none',
    basePrice = 0,
    currency = 'USD',
    includedInstructorSeats = 0,
    includedContentPackIds = [],
    addOnEligibleContentPackIds = [],
    featureFlags = {},
  } = {}
) {
  return addDoc(collection(db, PLATFORM_COLLECTIONS.SUBSCRIPTION_PLANS), {
    code,
    name,
    description,
    tier,
    active,
    billingInterval,
    basePrice,
    currency,
    includedInstructorSeats,
    includedContentPackIds,
    addOnEligibleContentPackIds,
    featureFlags,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getSubscriptionPlans(db) {
  const snapshot = await getDocs(collection(db, PLATFORM_COLLECTIONS.SUBSCRIPTION_PLANS));
  return (snapshot?.docs || []).map((docSnap) =>
    normalizeSubscriptionPlan({ id: docSnap.id, ...docSnap.data() })
  );
}

export async function getOrganizationSubscription(db, organizationId = '') {
  if (!organizationId) return null;

  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_SUBSCRIPTIONS,
    field: 'organizationId',
    value: organizationId,
  });

  if (!records?.length) return null;

  const prioritized = [...records]
    .map((record) => normalizeOrganizationSubscription(record))
    .sort((a, b) => {
      const rank = (status) =>
        ({
          active: 0,
          trialing: 1,
          past_due: 2,
          canceled: 3,
          expired: 4,
          inactive: 5,
        }[status] ?? 99);
      return rank(a.status) - rank(b.status);
    });

  return prioritized[0] || null;
}

export async function upsertOrganizationSubscription(
  db,
  {
    subscriptionId = '',
    organizationId = '',
    planCode = '',
    subscriptionPlanId = '',
    status = 'active',
    billingInterval = 'none',
    seatCount = 0,
    instructorSeatLimit = 0,
    trialStartAt = null,
    trialEndAt = null,
    currentPeriodStartAt = null,
    currentPeriodEndAt = null,
    cancelAtPeriodEnd = false,
    entitlementSourceType = '',
    entitlementSourceId = '',
  } = {}
) {
  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  const payload = {
    organizationId,
    planCode,
    subscriptionPlanId,
    status,
    billingInterval,
    seatCount,
    instructorSeatLimit,
    trialStartAt,
    trialEndAt,
    currentPeriodStartAt,
    currentPeriodEndAt,
    cancelAtPeriodEnd,
    entitlementSourceType,
    entitlementSourceId,
    updatedAt: serverTimestamp(),
  };

  const existingSubscription =
    subscriptionId ? { organizationSubscriptionId: subscriptionId } : await getOrganizationSubscription(db, organizationId);

  if (existingSubscription?.organizationSubscriptionId) {
    await updateDoc(
      doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_SUBSCRIPTIONS, existingSubscription.organizationSubscriptionId),
      payload
    );
    return {
      ...normalizeOrganizationSubscription(existingSubscription),
      ...payload,
    };
  }

  const docRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.ORGANIZATION_SUBSCRIPTIONS), {
    ...payload,
    createdAt: serverTimestamp(),
  });

  return {
    organizationSubscriptionId: docRef.id,
    id: docRef.id,
    ...payload,
  };
}

export async function startOrganizationTrial(
  db,
  {
    organizationId = '',
    planCode = '',
    subscriptionPlanId = '',
    trialStartAt = null,
    trialEndAt = null,
    seatCount = 0,
    instructorSeatLimit = 0,
  } = {}
) {
  return upsertOrganizationSubscription(db, {
    organizationId,
    planCode,
    subscriptionPlanId,
    status: 'trialing',
    billingInterval: 'none',
    seatCount,
    instructorSeatLimit,
    trialStartAt,
    trialEndAt,
    cancelAtPeriodEnd: false,
    entitlementSourceType: 'trial',
    entitlementSourceId: planCode || subscriptionPlanId,
  });
}

export async function setOrganizationPlan(
  db,
  {
    organizationId = '',
    planCode = '',
    subscriptionPlanId = '',
    billingInterval = 'monthly',
    seatCount = 0,
    instructorSeatLimit = 0,
    currentPeriodStartAt = null,
    currentPeriodEndAt = null,
  } = {}
) {
  return upsertOrganizationSubscription(db, {
    organizationId,
    planCode,
    subscriptionPlanId,
    status: 'active',
    billingInterval,
    seatCount,
    instructorSeatLimit,
    currentPeriodStartAt,
    currentPeriodEndAt,
    cancelAtPeriodEnd: false,
    entitlementSourceType: 'subscription',
    entitlementSourceId: planCode || subscriptionPlanId,
  });
}

export async function cancelOrganizationSubscription(
  db,
  {
    organizationId = '',
    subscriptionId = '',
    cancelAtPeriodEnd = true,
    status = 'canceled',
  } = {}
) {
  const existingSubscription =
    subscriptionId ? { organizationSubscriptionId: subscriptionId } : await getOrganizationSubscription(db, organizationId);

  if (!existingSubscription?.organizationSubscriptionId) {
    throw new Error('organization subscription not found');
  }

  await updateDoc(
    doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_SUBSCRIPTIONS, existingSubscription.organizationSubscriptionId),
    {
      status,
      cancelAtPeriodEnd,
      updatedAt: serverTimestamp(),
    }
  );
}

export async function syncOrganizationSubscriptionEntitlements(
  db,
  {
    organizationId = '',
    subscription = null,
    plans = [],
    existingEntitlements = [],
    grantedByUserId = '',
  } = {}
) {
  const normalizedSubscription = normalizeOrganizationSubscription(subscription);
  const desiredEntitlements = deriveEntitlementsFromSubscription(normalizedSubscription, plans);
  const normalizedExisting = (existingEntitlements || []).map((entitlement) =>
    normalizeEntitlementRecord(entitlement)
  );

  const orgExisting = normalizedExisting.filter(
    (entitlement) => entitlement.organizationId === organizationId
  );

  await Promise.all(
    desiredEntitlements.map(async (desiredEntitlement) => {
      const existingMatch = orgExisting.find(
        (entitlement) => entitlement.contentPackId === desiredEntitlement.contentPackId
      );

      if (existingMatch?.entitlementId) {
        await updateDoc(doc(db, PLATFORM_COLLECTIONS.ENTITLEMENTS, existingMatch.entitlementId), {
          ...desiredEntitlement,
          grantedByUserId,
          updatedAt: serverTimestamp(),
        });
        return;
      }

      await addDoc(collection(db, PLATFORM_COLLECTIONS.ENTITLEMENTS), {
        ...desiredEntitlement,
        grantedByUserId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    })
  );

  await Promise.all(
    orgExisting
      .filter((entitlement) =>
        ['subscription', 'trial'].includes(entitlement.sourceType) &&
        !desiredEntitlements.some((desired) => desired.contentPackId === entitlement.contentPackId) &&
        entitlement.status === 'active'
      )
      .map((entitlement) =>
        updateDoc(doc(db, PLATFORM_COLLECTIONS.ENTITLEMENTS, entitlement.entitlementId), {
          status: 'expired',
          updatedAt: serverTimestamp(),
        })
      )
  );
}

export function getOrganizationCommercialState(
  organizationId = '',
  {
    subscription = null,
    subscriptionPlans = [],
    entitlements = [],
    contentPacks = [],
  } = {}
) {
  const normalizedSubscription = subscription
    ? normalizeOrganizationSubscription(subscription)
    : null;
  const normalizedPlans = (subscriptionPlans || []).map((plan) => normalizeSubscriptionPlan(plan));
  const normalizedPacks = (contentPacks || []).map((contentPack) => normalizeContentPackRecord(contentPack));
  const normalizedEntitlements = (entitlements || [])
    .map((entitlement) => normalizeEntitlementRecord(entitlement))
    .filter((entitlement) => entitlement.organizationId === organizationId && entitlement.status !== 'revoked');

  const currentPlan = normalizedPlans.find(
    (plan) =>
      plan.code === normalizedSubscription?.planCode ||
      plan.subscriptionPlanId === normalizedSubscription?.subscriptionPlanId
  ) || null;

  const includedPackIds = currentPlan?.includedContentPackIds || [];
  const includedPacks = normalizedPacks.filter((contentPack) =>
    includedPackIds.includes(contentPack.contentPackId)
  );
  const activeEntitlementPacks = normalizedPacks.filter((contentPack) =>
    normalizedEntitlements.some((entitlement) => entitlement.contentPackId === contentPack.contentPackId)
  );
  const addOnPacks = activeEntitlementPacks.filter(
    (contentPack) => !includedPackIds.includes(contentPack.contentPackId)
  );
  const entitlementSources = normalizedEntitlements.map((entitlement) => ({
    ...entitlement,
    sourceMetadata: getEntitlementSourceMetadata(entitlement, {
      subscription: normalizedSubscription,
      plan: currentPlan,
    }),
  }));

  return {
    subscription: normalizedSubscription,
    currentPlan,
    includedPacks,
    addOnPacks,
    entitlementSources,
    seatCount: normalizedSubscription?.seatCount || 0,
    instructorSeatLimit:
      normalizedSubscription?.instructorSeatLimit ||
      currentPlan?.includedInstructorSeats ||
      0,
    isTrialing: normalizedSubscription?.status === 'trialing',
  };
}
